/**
 * Centralized localization selector for catalog & industry data.
 *
 * A5 — the visible production bug was Turkish `name/category/unit`
 * displayed on the English UI. Backend rows already carry bilingual
 * fields (`name_en`, `category_en`, `unit_en`); this helper picks the
 * right one at render time.
 *
 * Contract:
 *   - When the active UI language is `en` AND the row carries a
 *     non-empty English string, return the English string.
 *   - Otherwise (TR mode, or English string missing/empty) return the
 *     original stored string.
 *   - User-created rows persist `name_en == name` on the backend, so
 *     custom items never get "translated" — the fallback returns the
 *     user's own Turkish string unchanged.
 *   - Snapshots that live inside a saved quote (quote.packages[].items)
 *     do NOT carry `_en` fields. Callers should feed catalog rows or
 *     industry rows to this helper — not quote line snapshots — so the
 *     history stays frozen in the language the user picked at save
 *     time.
 *
 * We intentionally accept `lang` as a parameter (rather than reading
 * i18next inside the helper) so React components can memoize with the
 * language they already have from `useTranslation()`.
 */

const EN = 'en';

/**
 * Pick the localized value of `field` on `row` for `lang`.
 * `field` is one of "name", "category", "unit".
 */
export function pickLocalizedField(row, field, lang) {
  if (!row || !field) return '';
  const base = row[field];
  if (lang !== EN) return base || '';
  const en = row[`${field}_en`];
  // Empty / missing English → fall back to the base (user string).
  if (en === undefined || en === null || String(en).trim() === '') {
    return base || '';
  }
  return en;
}

/** Shortcut for the most common surface — catalog item names. */
export function pickCatalogName(row, lang) {
  return pickLocalizedField(row, 'name', lang);
}

/**
 * Industry name selector. The backend `/api/quote/industries` endpoint
 * already swaps `name` to English when the profile language is EN, so
 * `row.name` is authoritative there — but callers that read the raw
 * `INDUSTRIES` shape (with `name` + `name_en`) can use this helper as
 * a fallback path.
 */
export function pickIndustryName(row, lang) {
  return pickLocalizedField(row, 'name', lang);
}
